/*
 * Claudia — 3rd Story storefront chat widget
 * ------------------------------------------------------------------
 * A single self-contained, vanilla JS + CSS chat widget that embeds
 * Claudia (the 3rd Story support assistant) on 3rdstory.com.au.
 *
 * No framework, no build step. Drop it on the page with:
 *   <script src="/path/to/claudia-widget.js" defer></script>
 *
 * It talks to the `claude-chat` Supabase Edge Function — the same
 * Haiku → Sonnet → Opus cascade the Hub uses. When the reply hands off
 * to a human (handoff=true), a contact form is shown that posts to the
 * `conversations` function's "escalate" action, which records the
 * escalation and emails the team server-side.
 *
 * Config can be overridden before this script loads via:
 *   window.CLAUDIA_WIDGET_CONFIG = { endpoint, anonKey, escalateUrl };
 *
 * The Supabase anon key below is the project's PUBLISHABLE key
 * (VITE_SUPABASE_ANON_KEY) — it is designed to be exposed in the
 * browser and is already shipped in the Hub's production bundle.
 */
(function () {
  "use strict";

  // Guard against double-injection (e.g. the script tag included twice).
  if (window.__claudiaWidgetLoaded) return;
  window.__claudiaWidgetLoaded = true;

  var CONFIG = window.CLAUDIA_WIDGET_CONFIG || {};
  var ENDPOINT =
    CONFIG.endpoint ||
    "https://xnzajuoqfrvyyuejwrss.supabase.co/functions/v1/claude-chat";
  var ANON_KEY =
    CONFIG.anonKey ||
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhuemFqdW9xZnJ2eXl1ZWp3cnNzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODAwMDcxODksImV4cCI6MjA5NTU4MzE4OX0.mjMUXJXR-IaYsjynh4kFIDV2UwTy38MB8gldzHI9EOo";
  // Escalations go to a sibling function on the same /functions/v1 base, so an
  // overridden `endpoint` (staging, local serve) carries both calls with it.
  var ESCALATE_URL =
    CONFIG.escalateUrl || ENDPOINT.replace(/\/[^/]*\/?$/, "") + "/conversations";

  // Greetings are chosen at open time: page-aware first (based on the storefront
  // path the widget is embedded on), otherwise time-of-day aware. Each bucket
  // rotates randomly between three warm, brief variations (no trailing list of
  // capabilities), in a 55+ friendly tone with Australian spelling.
  function pickFrom(list) {
    return list[Math.floor(Math.random() * list.length)];
  }

  function pickGreeting() {
    var path = (window.location.pathname || "").toLowerCase();

    // ── Page-aware (Option B) ──
    if (path.indexOf("/products/") !== -1) {
      return pickFrom([
        "Hi there! If you'd like a hand with the sizing or colours on this piece, just ask.",
        "Hello! Happy to help with the size or colour for this one \u2014 what would you like to know?",
        "Hi! Any questions about the fit, sizing or colours here? I'm glad to help.",
      ]);
    }
    if (path.indexOf("/collections/") !== -1) {
      return pickFrom([
        "Hello! Looking for something in particular? I'd be glad to help you find it.",
        "Hi there! Tell me what you're after and I'll help you track it down.",
        "Hi! If you're after something specific, I'm happy to help you find it.",
      ]);
    }
    if (path.indexOf("/pages/returns-policy") !== -1 || path.indexOf("/a/returns") !== -1) {
      return pickFrom([
        "Hi there! Need a hand with a return or exchange? I'm happy to help.",
        "Hello! Happy to walk you through our returns and exchanges \u2014 just ask.",
        "Hi! If you have a question about returning or exchanging something, I'm here to help.",
      ]);
    }

    // ── Time-aware (Option C) — all other pages ──
    var hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return pickFrom([
        "Good morning! I'm Claudia from 3rd Story \u2014 here to help with sizing, orders, returns, or anything else you need.",
        "Good morning! I'm Claudia, happy to help with your order or any questions today.",
        "Good morning! I'm Claudia from 3rd Story \u2014 what can I help you with today, whether it's an order or a product question?",
      ]);
    }
    if (hour >= 12 && hour < 17) {
      return pickFrom([
        "Good afternoon! I'm Claudia from 3rd Story \u2014 here to help with sizing, orders, returns, or anything else you need.",
        "Good afternoon! I'm Claudia, happy to help with your order or any questions today.",
        "Good afternoon! I'm Claudia from 3rd Story \u2014 what can I do for you, whether it's an order or finding the right piece?",
      ]);
    }
    if (hour >= 17 && hour < 22) {
      return pickFrom([
        "Good evening! I'm Claudia from 3rd Story \u2014 what can I help you with tonight?",
        "Good evening! I'm Claudia, happy to help with your order or any questions this evening.",
        "Good evening! I'm Claudia from 3rd Story \u2014 here to help with sizing, orders, returns, or anything else you need.",
      ]);
    }
    // Late night (10pm–5am)
    return pickFrom([
      "Hello, you're up late! I'm Claudia from 3rd Story \u2014 happy to help with your order or any questions.",
      "Hi there! It's a little late, but I'm Claudia and I can help with your order, sizing, or anything else you need.",
      "Hello! I'm Claudia from 3rd Story \u2014 here to help with orders, returns, or any questions, even at this hour.",
    ]);
  }

  // ── State ───────────────────────────────────────────────────────
  // turns: { role: "user" | "assistant", content, displayOnly? }
  // The greeting is displayOnly so it is shown but NOT sent to the API
  // (the conversation sent to the model must begin with a user turn).
  var state = { open: false, busy: false, turns: [] };

  // ── Session id ──────────────────────────────────────────────────
  // One session per visitor per day. The id is stored with the date it was
  // minted, so a customer who comes back a few hours later continues the same
  // conversation, and a new day starts a fresh one. Nothing changes server-side
  // — the widget just stops sending a brand-new id on every page load.
  var SESSION_KEY = "claudia_session";

  // Local calendar date as YYYY-MM-DD. Deliberately not toISOString(), which is
  // UTC — that would roll the day over mid-afternoon for our Australian
  // customers and split one shopping session in two.
  function todayStamp() {
    var d = new Date();
    var mo = String(d.getMonth() + 1);
    var day = String(d.getDate());
    return (
      d.getFullYear() +
      "-" +
      (mo.length < 2 ? "0" + mo : mo) +
      "-" +
      (day.length < 2 ? "0" + day : day)
    );
  }

  // randomUUID needs a secure context, so keep a plain fallback for http pages
  // and older browsers.
  function newSessionId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "s-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
  }

  // Every localStorage touch is guarded: in private browsing, with cookies
  // blocked, or when the quota is full, reads and writes throw. In that case we
  // simply use the freshly minted id for this page load, which is exactly how
  // the widget behaved before.
  function loadSessionId() {
    var date = todayStamp();
    var fresh = newSessionId();
    try {
      var saved = JSON.parse(window.localStorage.getItem(SESSION_KEY) || "null");
      if (saved && saved.id && saved.date === date) return saved.id;
    } catch (e) {
      // Unreadable storage or a corrupt entry — fall through and mint a new id.
    }
    try {
      window.localStorage.setItem(SESSION_KEY, JSON.stringify({ id: fresh, date: date }));
    } catch (e) {
      // Unwritable storage — `fresh` lives for this page load only.
    }
    return fresh;
  }

  var sessionId = loadSessionId();

  // ── Styles ──────────────────────────────────────────────────────
  var CSS =
    '.cw-root,.cw-root *{box-sizing:border-box}' +
    '.cw-root{' +
    "font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
    "--cw-ink:#111;--cw-paper:#fff;--cw-muted:#8a8a8a;--cw-line:#ececec;" +
    "--cw-bot:#f4f4f3;--cw-danger:#b42318;--cw-ok:#0a7d52;" +
    "color:var(--cw-ink);-webkit-font-smoothing:antialiased}" +

    // Floating launcher button
    ".cw-launcher{position:fixed;right:20px;bottom:20px;width:56px;height:56px;" +
    "border-radius:50%;border:none;background:var(--cw-ink);color:#fff;cursor:pointer;" +
    "display:flex;align-items:center;justify-content:center;padding:0;" +
    "box-shadow:0 6px 24px rgba(0,0,0,.24);z-index:2147483000;" +
    "transition:transform .18s ease,box-shadow .18s ease}" +
    ".cw-launcher:hover{transform:translateY(-2px);box-shadow:0 10px 28px rgba(0,0,0,.3)}" +
    ".cw-launcher:active{transform:translateY(0)}" +
    ".cw-launcher svg{width:26px;height:26px;display:block}" +

    // Panel
    ".cw-panel{position:fixed;right:20px;bottom:88px;width:380px;" +
    "height:min(600px,calc(100vh - 120px));background:var(--cw-paper);" +
    "border:1px solid var(--cw-line);border-radius:16px;overflow:hidden;" +
    "display:flex;flex-direction:column;z-index:2147483000;" +
    "box-shadow:0 18px 50px rgba(0,0,0,.22);" +
    "opacity:0;transform:translateY(12px) scale(.98);pointer-events:none;" +
    "transition:opacity .2s ease,transform .2s ease}" +
    ".cw-panel.cw-show{opacity:1;transform:translateY(0) scale(1);pointer-events:auto}" +

    // Header
    ".cw-header{display:flex;align-items:center;gap:10px;padding:14px 16px;" +
    "border-bottom:1px solid var(--cw-line);background:var(--cw-paper);flex:none}" +
    ".cw-avatar{width:34px;height:34px;border-radius:50%;background:var(--cw-ink);" +
    "color:#fff;display:flex;align-items:center;justify-content:center;" +
    "font-weight:600;font-size:15px;flex:none}" +
    ".cw-title{font-size:15px;font-weight:600;line-height:1.2}" +
    ".cw-sub{font-size:12px;color:var(--cw-muted);line-height:1.2;margin-top:2px}" +
    ".cw-close{margin-left:auto;border:none;background:none;cursor:pointer;" +
    "color:var(--cw-muted);width:30px;height:30px;border-radius:8px;display:flex;" +
    "align-items:center;justify-content:center;padding:0}" +
    ".cw-close:hover{background:var(--cw-bot);color:var(--cw-ink)}" +
    ".cw-close svg{width:18px;height:18px}" +

    // Messages
    ".cw-msgs{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:10px;" +
    "background:var(--cw-paper);-webkit-overflow-scrolling:touch}" +
    ".cw-row{display:flex;width:100%}" +
    ".cw-row.cw-user{justify-content:flex-end}" +
    ".cw-row.cw-bot{justify-content:flex-start}" +
    ".cw-bubble{max-width:82%;padding:10px 14px;border-radius:16px;font-size:14px;" +
    "line-height:1.5;white-space:pre-wrap;word-wrap:break-word;overflow-wrap:anywhere}" +
    ".cw-user .cw-bubble{background:var(--cw-ink);color:#fff;border-bottom-right-radius:4px}" +
    ".cw-bot .cw-bubble{background:var(--cw-bot);color:#1a1a1a;border-bottom-left-radius:4px}" +
    ".cw-bubble a{color:inherit;text-decoration:underline;text-underline-offset:2px}" +
    ".cw-img{display:block;width:200px;max-width:100%;height:auto;border-radius:10px;" +
    "margin:4px 0;border:1px solid var(--cw-line)}" +

    // Typing indicator
    ".cw-typing{display:inline-flex;align-items:center;gap:4px;padding:13px 14px}" +
    ".cw-dot{width:7px;height:7px;border-radius:50%;background:var(--cw-muted);" +
    "animation:cw-blink 1.3s infinite ease-in-out}" +
    ".cw-dot:nth-child(2){animation-delay:.18s}.cw-dot:nth-child(3){animation-delay:.36s}" +
    "@keyframes cw-blink{0%,80%,100%{opacity:.25;transform:translateY(0)}" +
    "40%{opacity:1;transform:translateY(-3px)}}" +

    // Error notice
    ".cw-error{align-self:stretch;background:rgba(180,35,24,.07);" +
    "border:1px solid rgba(180,35,24,.28);color:var(--cw-danger);font-size:12.5px;" +
    "padding:9px 12px;border-radius:8px}" +

    // Contact form (handoff)
    ".cw-form{align-self:stretch;background:var(--cw-bot);border:1px solid var(--cw-line);" +
    "border-radius:12px;padding:14px;display:flex;flex-direction:column;gap:8px}" +
    ".cw-form-title{font-size:13.5px;font-weight:600}" +
    ".cw-field{width:100%;font-size:13.5px;padding:9px 11px;border:1px solid var(--cw-line);" +
    "border-radius:8px;background:#fff;color:var(--cw-ink);font-family:inherit}" +
    ".cw-field:focus{outline:none;border-color:var(--cw-ink)}" +
    "textarea.cw-field{min-height:96px;resize:vertical}" +
    ".cw-form-btn{align-self:flex-start;border:none;border-radius:8px;cursor:pointer;" +
    "background:var(--cw-ink);color:#fff;font-size:13.5px;font-weight:500;padding:9px 16px}" +
    ".cw-form-btn:disabled{opacity:.5;cursor:default}" +
    ".cw-ok{align-self:stretch;background:rgba(10,125,82,.08);" +
    "border:1px solid rgba(10,125,82,.3);color:var(--cw-ok);font-size:13px;" +
    "padding:11px 13px;border-radius:8px}" +

    // Input area
    ".cw-input-area{flex:none;display:flex;align-items:flex-end;gap:8px;padding:12px;" +
    "border-top:1px solid var(--cw-line);background:var(--cw-paper)}" +
    ".cw-input{flex:1;border:1px solid var(--cw-line);border-radius:20px;" +
    "padding:10px 14px;font-size:14px;font-family:inherit;line-height:1.4;resize:none;" +
    "max-height:120px;outline:none;color:var(--cw-ink);background:#fff}" +
    ".cw-input:focus{border-color:var(--cw-ink)}" +
    ".cw-send{flex:none;width:40px;height:40px;border-radius:50%;border:none;cursor:pointer;" +
    "background:var(--cw-ink);color:#fff;display:flex;align-items:center;justify-content:center;padding:0}" +
    ".cw-send:disabled{opacity:.35;cursor:default}" +
    ".cw-send svg{width:18px;height:18px}" +

    // Mobile: fullscreen panel
    "@media (max-width:480px){" +
    ".cw-panel{right:0;bottom:0;width:100%;height:100%;height:100dvh;" +
    "border-radius:0;border:none}" +
    ".cw-launcher{right:16px;bottom:16px}}";

  // ── Small DOM helpers ───────────────────────────────────────────
  function el(tag, cls, attrs) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (attrs) for (var k in attrs) node.setAttribute(k, attrs[k]);
    return node;
  }
  function svg(paths, extra) {
    var ns = "http://www.w3.org/2000/svg";
    var s = document.createElementNS(ns, "svg");
    s.setAttribute("viewBox", "0 0 24 24");
    s.setAttribute("fill", "none");
    s.setAttribute("stroke", "currentColor");
    s.setAttribute("stroke-width", "2");
    s.setAttribute("stroke-linecap", "round");
    s.setAttribute("stroke-linejoin", "round");
    if (extra) for (var k in extra) s.setAttribute(k, extra[k]);
    paths.forEach(function (d) {
      var p = document.createElementNS(ns, "path");
      p.setAttribute("d", d);
      s.appendChild(p);
    });
    return s;
  }

  // A Shopify CDN URL (exact host) — these are the only URLs we render as images.
  var SHOPIFY_CDN = /^https:\/\/cdn\.shopify\.com\//i;

  /* Append text to `parent`, turning any https:// URL into a safe node:
     - a cdn.shopify.com URL → an <img> (product image), sized via ?width=200
     - any other URL          → a plain text anchor (links only — never an image,
                                 to stay consistent with the hallucination guard)
     Text is added via textContent / text nodes only — never innerHTML — so
     customer and model content can't inject markup. */
  function appendLinkified(parent, text) {
    var re = /https:\/\/[^\s]+/g;
    var last = 0;
    var m;
    while ((m = re.exec(text)) !== null) {
      var start = m.index;
      var url = m[0];
      // Don't pull trailing sentence punctuation / closing paren into the URL.
      var trail = (url.match(/[.,;:!?)\]]+$/) || [""])[0];
      if (trail) url = url.slice(0, url.length - trail.length);
      if (start > last) parent.appendChild(document.createTextNode(text.slice(last, start)));

      if (SHOPIFY_CDN.test(url)) {
        // Render the product image above the surrounding text (block element).
        // Add width=200 as a proper query param (these URLs already carry ?v=…).
        var img = el("img", "cw-img");
        img.src = url + (url.indexOf("?") === -1 ? "?width=200" : "&width=200");
        img.alt = "";
        img.loading = "lazy";
        parent.appendChild(img);
      } else {
        var a = el("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = url;
        parent.appendChild(a);
      }

      if (trail) parent.appendChild(document.createTextNode(trail));
      last = start + m[0].length;
    }
    if (last < text.length) parent.appendChild(document.createTextNode(text.slice(last)));
  }

  // ── Build the UI ────────────────────────────────────────────────
  var root = el("div", "cw-root");

  var style = document.createElement("style");
  style.textContent = CSS;
  root.appendChild(style);

  // Launcher
  var launcher = el("button", "cw-launcher", {
    type: "button",
    "aria-label": "Open chat with 3rd Story support",
  });
  var iconChat = svg([
    "M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z",
  ]);
  var iconClose = svg(["M18 6 6 18", "M6 6l12 12"]);
  launcher.appendChild(iconChat);

  // Panel
  var panel = el("div", "cw-panel", { role: "dialog", "aria-label": "3rd Story support chat" });

  var header = el("div", "cw-header");
  var avatar = el("div", "cw-avatar");
  avatar.textContent = "C";
  var hText = el("div");
  var hTitle = el("div", "cw-title");
  hTitle.textContent = "Claudia";
  var hSub = el("div", "cw-sub");
  hSub.textContent = "3rd Story Support";
  hText.appendChild(hTitle);
  hText.appendChild(hSub);
  var closeBtn = el("button", "cw-close", { type: "button", "aria-label": "Close chat" });
  closeBtn.appendChild(svg(["M18 6 6 18", "M6 6l12 12"]));
  header.appendChild(avatar);
  header.appendChild(hText);
  header.appendChild(closeBtn);

  var msgs = el("div", "cw-msgs", { "aria-live": "polite" });

  var inputArea = el("form", "cw-input-area");
  var input = el("textarea", "cw-input", {
    rows: "1",
    placeholder: "Type your message…",
    "aria-label": "Type your message",
  });
  var sendBtn = el("button", "cw-send", { type: "submit", "aria-label": "Send message" });
  sendBtn.appendChild(svg(["M22 2 11 13", "M22 2l-7 20-4-9-9-4 20-7z"]));
  inputArea.appendChild(input);
  inputArea.appendChild(sendBtn);

  panel.appendChild(header);
  panel.appendChild(msgs);
  panel.appendChild(inputArea);

  root.appendChild(launcher);
  root.appendChild(panel);

  // ── Rendering ───────────────────────────────────────────────────
  function scrollToBottom() {
    msgs.scrollTop = msgs.scrollHeight;
  }

  function renderTurn(turn) {
    var row = el("div", "cw-row " + (turn.role === "user" ? "cw-user" : "cw-bot"));
    var bubble = el("div", "cw-bubble");
    if (turn.role === "user") bubble.textContent = turn.content;
    else appendLinkified(bubble, turn.content);
    row.appendChild(bubble);
    msgs.appendChild(row);
    scrollToBottom();
  }

  function addTurn(role, content, opts) {
    var turn = { role: role, content: content };
    if (opts && opts.displayOnly) turn.displayOnly = true;
    state.turns.push(turn);
    renderTurn(turn);
  }

  var typingEl = null;
  function showTyping() {
    if (typingEl) return;
    typingEl = el("div", "cw-row cw-bot");
    var bubble = el("div", "cw-bubble cw-typing");
    bubble.appendChild(el("span", "cw-dot"));
    bubble.appendChild(el("span", "cw-dot"));
    bubble.appendChild(el("span", "cw-dot"));
    typingEl.appendChild(bubble);
    msgs.appendChild(typingEl);
    scrollToBottom();
  }
  function hideTyping() {
    if (typingEl && typingEl.parentNode) typingEl.parentNode.removeChild(typingEl);
    typingEl = null;
  }

  function showError(message) {
    var note = el("div", "cw-error");
    note.textContent = message || "Something went wrong. Please try again.";
    msgs.appendChild(note);
    scrollToBottom();
  }

  // ── Conversation summary (fallback for the contact form body) ────
  function clientSummary() {
    var lines = state.turns
      .filter(function (t) {
        return !t.displayOnly;
      })
      .map(function (t) {
        return (t.role === "user" ? "Me" : "Claudia (AI)") + ": " + t.content;
      });
    return [
      "I was chatting with your AI assistant and would like a person to follow up.",
      "",
      "Conversation so far:",
    ]
      .concat(lines)
      .join("\n");
  }

  // ── Edge function POST ───────────────────────────────────────────
  // Resolves with the parsed body only when the function actually returned OK;
  // rejects with the function's own error message otherwise.
  function postJson(url, payload) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + ANON_KEY,
        apikey: ANON_KEY,
      },
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok || (data && data.error)) {
            throw new Error((data && data.error) || "HTTP " + res.status);
          }
          return data;
        });
    });
  }

  /* Contact form shown after a handoff — mirrors SupportChat.tsx. Submits to the
     `conversations` function's "escalate" action, which records the escalation
     and emails the team server-side. The response is readable (unlike the old
     no-cors post to Shopify's /contact), so we only confirm on a real OK. */
  function renderContactForm(summary) {
    // Only ever show one contact form at a time.
    var existing = msgs.querySelector(".cw-form, .cw-ok");
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var form = el("form", "cw-form");
    var title = el("div", "cw-form-title");
    title.textContent = "Leave your details and our team will follow up";

    var name = el("input", "cw-field", { type: "text", placeholder: "Your name", required: "" });
    var email = el("input", "cw-field", { type: "email", placeholder: "Your email", required: "" });
    var message = el("textarea", "cw-field", { placeholder: "Your message", required: "" });
    message.value = (summary && summary.trim()) || clientSummary();

    var err = el("div");
    err.style.cssText = "font-size:12px;color:var(--cw-danger);display:none";

    var btn = el("button", "cw-form-btn", { type: "submit" });
    btn.textContent = "Send to our team";

    form.appendChild(title);
    form.appendChild(name);
    form.appendChild(email);
    form.appendChild(message);
    form.appendChild(err);
    form.appendChild(btn);
    msgs.appendChild(form);
    scrollToBottom();

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (btn.disabled) return;
      if (!name.value.trim() || !email.value.trim() || !message.value.trim()) return;
      btn.disabled = true;
      btn.textContent = "Sending…";
      err.style.display = "none";

      postJson(ESCALATE_URL, {
        action: "escalate",
        sessionId: sessionId,
        channel: "widget",
        name: name.value.trim(),
        email: email.value.trim(),
        message: message.value.trim(),
        pageUrl: window.location.href,
      })
        .then(function () {
          var ok = el("div", "cw-ok");
          ok.textContent =
            "Thanks! Your message has been sent to our team \u2014 they'll be in touch by email.";
          if (form.parentNode) form.parentNode.replaceChild(ok, form);
          scrollToBottom();
        })
        .catch(function (e2) {
          err.textContent = (e2 && e2.message) || "Couldn't send \u2014 please try again.";
          err.style.display = "block";
          btn.disabled = false;
          btn.textContent = "Send to our team";
        });
    });
  }

  // ── Send flow ───────────────────────────────────────────────────
  function setBusy(b) {
    state.busy = b;
    sendBtn.disabled = b;
    input.disabled = b;
  }

  function autosize() {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 120) + "px";
  }

  function send(text) {
    text = (text || "").trim();
    if (!text || state.busy) return;

    addTurn("user", text);
    input.value = "";
    autosize();
    setBusy(true);
    showTyping();

    var payload = {
      messages: state.turns
        .filter(function (t) {
          return !t.displayOnly;
        })
        .map(function (t) {
          return { role: t.role, content: t.content };
        }),
      context: "Channel: Storefront",
      sessionId: sessionId,
    };

    postJson(ENDPOINT, payload)
      .then(function (data) {
        hideTyping();
        addTurn("assistant", data.reply);
        if (data.handoff) renderContactForm(data.summary);
      })
      .catch(function (e) {
        hideTyping();
        showError(e instanceof Error ? e.message : String(e));
      })
      .finally(function () {
        setBusy(false);
        if (state.open) input.focus();
      });
  }

  // ── Open / close ────────────────────────────────────────────────
  function open() {
    state.open = true;
    panel.classList.add("cw-show");
    launcher.replaceChild(iconClose, launcher.firstChild);
    launcher.setAttribute("aria-label", "Close chat");
    // Greet on first open.
    if (state.turns.length === 0) addTurn("assistant", pickGreeting(), { displayOnly: true });
    setTimeout(function () {
      input.focus();
    }, 60);
  }
  function close() {
    state.open = false;
    panel.classList.remove("cw-show");
    launcher.replaceChild(iconChat, launcher.firstChild);
    launcher.setAttribute("aria-label", "Open chat with 3rd Story support");
  }
  function toggle() {
    if (state.open) close();
    else open();
  }

  // ── Events ──────────────────────────────────────────────────────
  launcher.addEventListener("click", toggle);
  closeBtn.addEventListener("click", close);

  inputArea.addEventListener("submit", function (e) {
    e.preventDefault();
    send(input.value);
  });
  input.addEventListener("input", autosize);
  input.addEventListener("keydown", function (e) {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input.value);
    }
  });
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && state.open) close();
  });

  // ── Mount ───────────────────────────────────────────────────────
  function mount() {
    document.body.appendChild(root);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();
